import FacebookIcon from "@src/app/assets/icons/facebook.svg?react";
import MailIcon from "@src/app/assets/icons/mail.svg?react";
import ShareIcon from "@src/app/assets/icons/share.svg?react";
import TelegramIcon from "@src/app/assets/icons/telegram.svg?react";
import TwitterIcon from "@src/app/assets/icons/twitter.svg?react";
import WechatIcon from "@src/app/assets/icons/wechat.svg?react";
import WhatsappIcon from "@src/app/assets/icons/whatsapp.svg?react";
import Button from "@src/app/components/Button/Button";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import viewportState from "@src/app/stores/viewport";
import cx from "classnames";
import type { ReactElement } from "react";
import { useEffect, useRef, useState } from "react";
import type { LinksFunction } from "react-router";

import CopyLinkButton from "./CopyLinkButton";
import { nativeShare, ShareToastContainer } from "./shareToastHelper";
import styles from "./SocialShare.css?url";
import SocialShareButton from "./SocialShareButton";
export const SocialShareLinks: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
];

interface WindowWithA2A extends Window {
  a2a?: {
    init: (type: string) => void;
  };
}

type Props = Readonly<{
  iconOnly?: boolean;
  isVideoArticle?: boolean;
  articleTitle?: string;
  articleUrl?: string;
  youtubeTitle?: string;
  className?: string;
  style?: React.CSSProperties;
  copyLinkMessagePosition?: "left" | "right";
}>;

export default function SocialShare({
  iconOnly,
  isVideoArticle,
  articleTitle,
  articleUrl,
  youtubeTitle,
  className,
  style,
  copyLinkMessagePosition = "right",
}: Props): ReactElement {
  const [isSocialShareVisible, setIsSocialShareVisible] = useState(false);
  const [hasCopiedLink, setHasCopiedLink] = useState(false);
  const socialWrapperRef = useRef<HTMLDivElement>(null);
  const { isMobileOrTabletDevice } = viewportState;
  const a2aClasses = "a2a_kit a2a_kit_size_32";
  const articlePath =
    typeof window !== "undefined" ? window.location.origin + articleUrl : "";
  const dataAttributes = isVideoArticle
    ? {
        "data-a2a-url": articlePath,
        "data-a2a-title": youtubeTitle,
      }
    : {};

  // Safe SSR title & url
  const safeTitle = articleTitle
    ? articleTitle
    : typeof document !== "undefined"
      ? (() => {
          const ogTitle = document
            .querySelector('meta[property="og:title"]')
            ?.getAttribute("content");
          return ogTitle || document.title || "";
        })()
      : "";

  const safeUrl = articleUrl
    ? articlePath
    : typeof window !== "undefined"
      ? window.location.href
      : "";

  // Toggle social share dropdown visibility
  const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSocialShareVisible((prev) => !prev);
    if (hasCopiedLink) {
      setHasCopiedLink(false);
    }
  };

  // Handle click
  const handleClickSocialShare = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    if (isMobileOrTabletDevice) {
      nativeShare({
        title: safeTitle,
        text: safeTitle,
        url: safeUrl,
        autoClose: 3000,
        toastTitle: "谢谢分享！",
        fallbackToastTitle: "链接已复制",
      });
      return;
    }
    toggleDropdown(event);
  };

  // Handle click outside to close dropdown
  const handleClickOutside = (event: MouseEvent) => {
    if (
      socialWrapperRef.current &&
      !socialWrapperRef.current.contains(event.target as Node)
    ) {
      setIsSocialShareVisible(false);
    }
  };

  const sendDataToGTM = useGTMDispatch();
  const handleClickGTM = (mediaType: string) => {
    sendDataToGTM({
      event: "custom_web_event",
      eventCategory: "AddToAny",
      eventLabel: window.location.href,
      eventAction: mediaType,
    });
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Load AddToAny script dynamically only when needed
  useEffect(() => {
    if (isSocialShareVisible) {
      const hideA2AModal = () => {
        const observer = new MutationObserver(() => {
          const overlay = document.querySelector(".a2a_overlay");
          if (!overlay) return;

          const displayStyle = getComputedStyle(overlay).display;
          if (displayStyle && displayStyle !== "none") {
            // stop observing
            observer.disconnect();
            setTimeout(() => {
              // click to trigger close the modals
              (overlay as HTMLElement).click();
            }, 3000);
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      };

      if (!document.getElementById("addtoany-script")) {
        (window as WindowWithA2A & { a2a_config?: unknown }).a2a_config = {
          locale: "custom",
          localize: {
            ThanksForSharing: "谢谢分享！",
          },
          thanks: {
            postShare: true,
            ad: false,
          },
        };

        const script = document.createElement("script");
        script.id = "addtoany-script";
        script.src = "https://static.addtoany.com/menu/page.js";
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
          const windowWithA2A = window as WindowWithA2A;
          windowWithA2A.a2a?.init("page");
          hideA2AModal();
        };
      } else {
        (window as WindowWithA2A).a2a?.init("page");
        hideA2AModal();
      }
    }
  }, [isSocialShareVisible]);
  return (
    <div
      data-testid="social-share-button"
      className={cx(
        "flex",
        {
          "flex-col-reverse gap-2": iconOnly,
          "flex-row gap-5": !iconOnly,
        },
        className,
      )}
      style={style}
    >
      <div className="relative">
        <Button
          variant="tertiary"
          size="medium"
          icon={<ShareIcon width={20} height={20} />}
          style={{
            touchAction: "manipulation",
          }}
          className="cursor-pointer"
          {...(isMobileOrTabletDevice
            ? { onPointerUp: handleClickSocialShare }
            : { onClick: handleClickSocialShare })}
        >
          {!iconOnly && <span>分享</span>}
        </Button>

        {!isMobileOrTabletDevice && isSocialShareVisible && (
          <div
            ref={socialWrapperRef}
            // right-0 bottom-12
            className={cx(
              a2aClasses,
              "absolute z-50 w-auto bg-white shadow-md flex flex-col text-body-2 font-medium p-2 ",
              {
                "-top-2 -left-2 rounded-full text-button-primary-label gap-2 animate-fade-in":
                  iconOnly,
                "bottom-11 right-0 rounded-[4px] text-button-secondary-label animate-fade-in-up":
                  !iconOnly,
              },
            )}
            data-testid="social-share-dropdown"
            {...dataAttributes}
          >
            {!iconOnly && <span>分享</span>}

            <SocialShareButton
              icon={<FacebookIcon width={20} height={20} />}
              label="脸书"
              iconOnly={iconOnly}
              mediaType="facebook"
              onClick={() => {
                setIsSocialShareVisible(false);
                handleClickGTM("facebook");
              }}
            />
            <SocialShareButton
              icon={<TwitterIcon width={20} height={20} />}
              label="X / 推特"
              iconOnly={iconOnly}
              mediaType="twitter"
              onClick={() => {
                setIsSocialShareVisible(false);
                handleClickGTM("twitter");
              }}
            />
            <SocialShareButton
              icon={<MailIcon width={20} height={20} />}
              label="邮件"
              iconOnly={iconOnly}
              mediaType="email"
              onClick={() => {
                setIsSocialShareVisible(false);
                handleClickGTM("email");
              }}
            />
            <SocialShareButton
              icon={<WechatIcon width={20} height={20} />}
              label="微信"
              iconOnly={iconOnly}
              mediaType="wechat"
              onClick={() => {
                setIsSocialShareVisible(false);
                handleClickGTM("wechat");
              }}
            />
            <SocialShareButton
              icon={<TelegramIcon width={20} height={20} />}
              label="电报"
              iconOnly={iconOnly}
              mediaType="telegram"
              onClick={() => {
                setIsSocialShareVisible(false);
                handleClickGTM("telegram");
              }}
            />
            <SocialShareButton
              icon={<WhatsappIcon width={20} height={20} />}
              label="WhatsApp"
              iconOnly={iconOnly}
              mediaType="whatsapp"
              onClick={() => {
                setIsSocialShareVisible(false);
                handleClickGTM("whatsapp");
              }}
            />
          </div>
        )}
      </div>

      <ShareToastContainer />

      <CopyLinkButton
        iconOnly={iconOnly}
        isVideoArticle={isVideoArticle}
        articleUrl={articlePath}
        setHasCopiedLink={setHasCopiedLink}
        hasCopiedLink={hasCopiedLink}
        statusMessagePosition={copyLinkMessagePosition}
      />
    </div>
  );
}
